<template>
  <BlockFieldset class="project-base-information">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t('employer')"
          label-for="employer"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('employer')"
            v-model="project.employer"
            id="employer"
            :options="employers"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
            track-by="number"
            label="name"
          >
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('inCharge')"
          label-for="inCharge"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            class="multiselect-users"
            :placeholder="'Mitarbeiter ' + $t('select') + '/suchen'"
            v-model="project.inCharge"
            id="inCharge"
            :options="filteredUsers"
            track-by="number"
            :internal-search="false"
            :show-labels="false"
            :allow-empty="true"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            v-on:search-change="onUserSearch"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }}
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('inChargeSales')"
          label-for="salesInCharge"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            class="multiselect-users"
            :placeholder="'Mitarbeiter ' + $t('select') + '/suchen'"
            v-model="salesInCharge"
            id="salesInCharge"
            :options="filteredUsers"
            track-by="number"
            :internal-search="false"
            :show-labels="false"
            :allow-empty="true"
            :disabled="true"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            v-on:search-change="onUserSearch"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }}
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('customerNumber')"
          label-for="customerNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.customer.number"
            type="text"
            :placeholder="$t('customerNumber')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('employerProjectNumber')"
          label-for="employerProjectNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.employerProjectNumber"
            type="text"
            :placeholder="$t('employerProjectNumber')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('deliveryAt')"
          label-for="deliveryAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            :disabled="isBlockReadOnly"
            v-model="deliveryAt"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('businessModelState')"
          label-for="businessModelState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('businessModelState')"
            v-model="project.businessModelState"
            id="businessModelState"
            :options="getEnumValues('BusinessModel')"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge variant="secondary">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge variant="secondary">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('projectTypeState')"
          label-for="projectTypeState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('projectTypeState')"
            v-model="project.projectTypeState"
            id="projectTypeState"
            :options="getEnumValues('ProjectTypeState')"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="projectTypeStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="projectTypeStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('projectState')"
          label-for="projectState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('projectState')"
            v-model="project.projectState"
            id="projectState"
            :options="getEnumValues('ProjectState')"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="projectStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="projectStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <b-form-group
          horizontal
          :label="$t('billingModelState')"
          label-for="billingModelState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('billingModelState')"
            v-model="project.billingModelState"
            id="billingModelState"
            :options="getEnumValues('BillingModel')"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge variant="secondary">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge variant="secondary">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('businessUnitState')"
          label-for="businessUnitState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('businessUnitState')"
            v-model="project.businessUnitState"
            id="businessUnitState"
            :options="getEnumValues('BusinessUnit')"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge variant="secondary">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge variant="secondary">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('contractSignedState')"
          label-for="contractSignedState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :disabled="isBlockReadOnly"
            :placeholder="$t('contractSignedState')"
            v-model="project.contractSignedState"
            id="contractSignedState"
            :options="optionsYesNo"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="yesNoColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="yesNoColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('planningHorizonYear')"
          label-for="planningHorizonYear"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            :disabled="isBlockReadOnly"
            v-model="planningHorizonYearCalendar"
            view="year"
            dateFormat="yy"
            :placeholder="$t('unit_year')"
            :yearNavigator="true"
            :manualInput="true"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('probabilityOfImplementation')"
          label-for="probabilityOfImplementation"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <UnitInputNumber
            id="probabilityOfImplementation"
            :disabled="isBlockReadOnly"
            v-model="project.probabilityOfImplementation"
            unit="unit_percent"
            :min="0"
            :max="100"
            :minFractionDigits="0"
            :maxFractionDigits="3"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('offerSentAt')"
          label-for="offerSentAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            :disabled="isBlockReadOnly"
            v-model="offerSentAt"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('orderConfirmationSentAt')"
          label-for="orderConfirmationSentAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            :disabled="isBlockReadOnly"
            v-model="orderConfirmationSentAt"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <div v-if="hasSaveButton" class="text-right mt-2">
      <b-button variant="bg" :disabled="isSaving" @click="save">
        {{ $t('saveNamed', { name: $t('project_base_information') }) }}
      </b-button>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import UnitInputNumber from '@/components/UnitInputNumber';
import { yesNo } from '@/helpers/enums';
import { getProjectStateColor, getYesNoStateColor, projectTypeStateColor } from '@/helpers/colors';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { toProjectBaseInformationInput } from './projectBaseInformationInput';

/** Number of the unsaved project (ProjectEdit `_new`): its fields go with `createProject`. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block project-base-information (plan docs/work/_done/2026-10-project-block-base-information):
 * base data of the project, saved through its own mutation with its own button. On the unsaved
 * project it has no save button; its values go with `createProject`. Emits `dirty` (boolean) and
 * `saved`.
 */
export default {
  name: 'ProjectBaseInformation',
  components: {
    BlockFieldset,
    Multiselect,
    Calendar,
    UnitInputNumber,
  },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
    /** Organization of the project; the employer list shows its employers only. */
    organization: { type: Object, default: null },
  },
  data() {
    return {
      filteredUsers: [],
      usersFilter: {
        pagination: {
          skip: 0,
          pageSize: 200,
        },
        sorting: {
          sortField: 'lastname',
          sortOrder: 1,
        },
        filterName: 'project-base-information-user-filters',
        filters: {
          userSearch: { value: null },
        },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['getCurrentUser', 'isClient', 'isOrganizationRoleNavigationEnabled']),
    ...mapGetters(['getEnumValues', 'getEmployers', 'getUsers']),
    blockInput() {
      return toProjectBaseInformationInput(this.project);
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && this.project.number !== NEW_PROJECT_NUMBER;
    },
    employers() {
      return this.getEmployers.filter(
        (employer) =>
          employer.client && this.organization && employer.client.id === this.organization.id
      );
    },
    optionsYesNo() {
      return yesNo();
    },
    deliveryAt: {
      get() {
        return this.$calendarFormat(this.project.deliveryAt);
      },
      set(value) {
        this.project.deliveryAt = value;
      },
    },
    offerSentAt: {
      get() {
        return this.$calendarFormat(this.project.offerSentAt);
      },
      set(value) {
        this.project.offerSentAt = value;
      },
    },
    orderConfirmationSentAt: {
      get() {
        return this.$calendarFormat(this.project.orderConfirmationSentAt);
      },
      set(value) {
        this.project.orderConfirmationSentAt = value;
      },
    },
    planningHorizonYearCalendar: {
      get() {
        const year = this.project.planningHorizonYear;
        return year != null && Number.isFinite(year) ? new Date(year, 0, 1) : null;
      },
      set(value) {
        if (value == null) {
          this.project.planningHorizonYear = null;
          return;
        }
        if (value instanceof Date && !isNaN(value.getTime())) {
          this.project.planningHorizonYear = value.getFullYear();
          return;
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
          this.project.planningHorizonYear = value;
          return;
        }
        const parsed = typeof value === 'string' ? parseInt(value, 10) : NaN;
        this.project.planningHorizonYear = Number.isFinite(parsed) ? parsed : null;
      },
    },
    salesInCharge: {
      get() {
        return this.project.sales ? this.project.sales.inCharge || null : null;
      },
      set(value) {
        if (!this.project.sales) {
          this.$set(this.project, 'sales', {});
        }
        this.project.sales.inCharge = value;
      },
    },
  },
  methods: {
    ...mapActions([
      'fetchEnumValues',
      'fetchEmployers',
      'fetchEmployersByClient',
      'fetchUsersPaginated',
      'updateProjectBaseInformation',
    ]),
    saveBlockInput(input) {
      return this.updateProjectBaseInformation({ projectNumber: this.project.number, input });
    },
    yesNoColor(state) {
      return getYesNoStateColor(state);
    },
    projectStateColor(state) {
      return getProjectStateColor(state);
    },
    projectTypeStateColor,
    async loadUserData() {
      await this.fetchUsersPaginated({
        pagination: this.usersFilter.pagination,
        sorting: this.usersFilter.sorting,
        filters: { ...this.usersFilter.filters },
      });
      this.filteredUsers = this.getUsers;
    },
    /** Search in the dropdown for a user to assign to the project. */
    async onUserSearch(rawQuery) {
      this.usersFilter.filters.userSearch.value = rawQuery.toLowerCase();
      await this.loadUserData();
    },
    async setup() {
      try {
        const usesEmployersOfClient = !this.isOrganizationRoleNavigationEnabled && this.isClient;
        const employersLoaded = usesEmployersOfClient
          ? this.fetchEmployersByClient(this.getCurrentUser.client.id)
          : this.fetchEmployers();
        await Promise.all([
          employersLoaded,
          this.fetchEnumValues('ProjectState'),
          this.fetchEnumValues('ProjectTypeState'),
          this.fetchEnumValues('BusinessModel'),
          this.fetchEnumValues('BillingModel'),
          this.fetchEnumValues('BusinessUnit'),
          this.loadUserData(),
        ]);
      } catch (error) {
        console.error('Error in setup:', error);
      }
    },
  },
  async mounted() {
    await this.setup();
  },
};
</script>

<style scoped lang="scss">
.multiselect-users {
  min-width: 230px;
  display: inline-block;
}

::v-deep .multiselect-users .multiselect__input {
  padding-top: 1em;
}
</style>
